import { Injectable, inject } from '@angular/core';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { BehaviorSubject } from 'rxjs';

import { AuthService } from '@auth/services/auth.service';
import { environment } from '@environments/environment';

export const POSTO_COMANDO_EVENTS = [
  'EscalaIniciar',
  'EscalaConcluir',
  'EfetivoLogin',
  'EfetivoAlimentacao',
  'EfetivoConsultarAlimentacao',
  'EfetivoOcorrencia',
] as const;

export type PostoComandoEvent = (typeof POSTO_COMANDO_EVENTS)[number];
export type NotificationConnectionState =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting';

export interface OcorrenciaHubDto {
  id: number;
  criticidadeEnum: number;
  tipoOcorrenciaEnum: number;
  descricaoTipoOutro?: string | null;
  dataOcorrencia: string;
  postoId: number;
  nomePosto: string;
  nomeEfetivo: string;
}

export interface OcorrenciaNotification extends OcorrenciaHubDto {
  key: string;
  lida: boolean;
}

export interface PostoComandoNotification {
  key: string;
  event: PostoComandoEvent;
  entityId?: string | number;
  title: string;
  description: string;
  occurredAt: string;
  postoId?: number;
  nomePosto?: string;
  efetivoId?: number;
  nomeEfetivo?: string;
  escalaId?: number;
  ocorrenciaId?: number;
  criticidadeEnum?: number;
  tipoOcorrenciaEnum?: number;
  descricaoTipoOutro?: string | null;
  lida: boolean;
  payload: Record<string, unknown>;
}

export interface NotificationDestination {
  commands: Array<string | number>;
  queryParams?: Record<string, string | number>;
}

type HubPayload = Record<string, unknown> | string | number | null | undefined;

const EVENT_META: Record<
  PostoComandoEvent,
  { title: string; icon: string; description: string }
> = {
  EscalaIniciar: {
    title: 'Escala iniciada',
    icon: 'ri-play-circle-line',
    description: 'Uma escala operacional foi iniciada.',
  },
  EscalaConcluir: {
    title: 'Escala concluída',
    icon: 'ri-checkbox-circle-line',
    description: 'Uma escala operacional foi concluída.',
  },
  EfetivoLogin: {
    title: 'Login de efetivo',
    icon: 'ri-login-circle-line',
    description: 'Um efetivo efetuou login no sistema.',
  },
  EfetivoAlimentacao: {
    title: 'Alimentação atualizada',
    icon: 'ri-restaurant-line',
    description: 'Foi atualizado um registo de alimentação de efetivo.',
  },
  EfetivoConsultarAlimentacao: {
    title: 'Consulta de alimentação',
    icon: 'ri-file-search-line',
    description: 'Foi realizada uma consulta ao fluxo de alimentação.',
  },
  EfetivoOcorrencia: {
    title: 'Nova ocorrência',
    icon: 'ri-alarm-warning-line',
    description: 'Foi registada uma nova ocorrência em ronda ou posto.',
  },
};

@Injectable({ providedIn: 'root' })
export class OcorrenciaNotificationService {
  private readonly auth = inject(AuthService);
  private readonly readStorageKey = 'sp_notificacoes_lidas';
  private readonly legacyReadStorageKey = 'sp_ocorrencias_lidas';
  private readonly readKeys = this.restoreReadKeys();
  private connection?: HubConnection;
  private connectionPromise?: Promise<void>;
  private retryTimer?: ReturnType<typeof setTimeout>;
  private shouldBeConnected = false;
  private sequence = 0;

  private readonly notificationsSubject =
    new BehaviorSubject<PostoComandoNotification[]>([]);
  readonly notifications$ = this.notificationsSubject.asObservable();

  private readonly occurrencesSubject =
    new BehaviorSubject<OcorrenciaNotification[]>([]);
  readonly occurrences$ = this.occurrencesSubject.asObservable();

  private readonly unreadCountSubject = new BehaviorSubject(0);
  readonly unreadCount$ = this.unreadCountSubject.asObservable();

  private readonly connectionStateSubject =
    new BehaviorSubject<NotificationConnectionState>('disconnected');
  readonly connectionState$ = this.connectionStateSubject.asObservable();

  async connect(): Promise<void> {
    this.shouldBeConnected = true;
    this.clearRetry();
    if (this.connection?.state === HubConnectionState.Connected) return;
    if (this.connectionPromise) return this.connectionPromise;
    this.connectionPromise = this.startConnection().finally(() => {
      this.connectionPromise = undefined;
    });
    return this.connectionPromise;
  }

  async disconnect(): Promise<void> {
    this.shouldBeConnected = false;
    this.connectionPromise = undefined;
    this.clearRetry();
    const connection = this.connection;
    this.connection = undefined;
    if (connection) await connection.stop();
    this.connectionStateSubject.next('disconnected');
  }

  // O contrato novo não define métodos de consulta/inscrição no cliente.
  async refresh(): Promise<void> {
    if (this.connection?.state !== HubConnectionState.Connected) {
      await this.connect();
    }
  }

  markAsRead(itemOrKey: PostoComandoNotification | string | number): void {
    let key: string;
    if (typeof itemOrKey === 'object') {
      key = itemOrKey.key;
    } else if (typeof itemOrKey === 'number') {
      key = this.notificationsSubject.value.find(
        (item) => item.ocorrenciaId === itemOrKey,
      )?.key ?? String(itemOrKey);
    } else {
      key = itemOrKey;
    }
    this.readKeys.add(key);
    this.persistReadKeys();
    this.updateReadState();
  }

  markAllAsRead(): void {
    this.notificationsSubject.value.forEach((item) => this.readKeys.add(item.key));
    this.persistReadKeys();
    this.updateReadState();
  }

  connectionStateLabel(state: NotificationConnectionState): string {
    return {
      disconnected: 'Desligado',
      connecting: 'A ligar',
      connected: 'Ligado',
      reconnecting: 'A religar',
    }[state];
  }

  eventLabel(event: PostoComandoEvent): string {
    return EVENT_META[event].title;
  }

  icon(item: PostoComandoNotification): string {
    return EVENT_META[item.event].icon;
  }

  isOccurrence(item: PostoComandoNotification): boolean {
    return item.event === 'EfetivoOcorrencia';
  }

  isUrgent(item: PostoComandoNotification): boolean {
    return this.isOccurrence(item) && item.criticidadeEnum === 3;
  }

  entityCode(item: PostoComandoNotification): string {
    if (item.ocorrenciaId != null) return 'OC-' + item.ocorrenciaId;
    if (item.escalaId != null) return 'ESC-' + item.escalaId;
    if (item.efetivoId != null) return 'EF-' + item.efetivoId;
    return this.eventLabel(item.event);
  }

  contextLabel(item: PostoComandoNotification): string {
    return item.nomePosto || item.nomeEfetivo || 'Posto de Comando';
  }

  destination(item: PostoComandoNotification): NotificationDestination {
    if (item.event === 'EfetivoOcorrencia') {
      return {
        commands: ['/admin/ocorrencias'],
        queryParams: item.ocorrenciaId != null
          ? { ocorrencia: item.ocorrenciaId }
          : undefined,
      };
    }
    if (item.event === 'EscalaIniciar' || item.event === 'EscalaConcluir') {
      return {
        commands: item.escalaId != null
          ? ['/admin/escalas', item.escalaId]
          : ['/admin/escalas'],
      };
    }
    return {
      commands: item.efetivoId != null
        ? ['/admin/efetivos', item.efetivoId]
        : ['/admin/efetivos'],
    };
  }

  criticidadeLabel(value?: number): string {
    if (value == null) return 'Informativo';
    return ({ 1: 'Alta', 2: 'Média', 3: 'Urgente' } as Record<number, string>)[value]
      ?? 'Criticidade ' + value;
  }

  tipoLabel(item: Pick<OcorrenciaHubDto, 'descricaoTipoOutro' | 'tipoOcorrenciaEnum'>): string {
    if (item.descricaoTipoOutro?.trim()) return item.descricaoTipoOutro.trim();
    return ({
      1: 'Incidente',
      2: 'Falha de equipamento',
      3: 'Intrusão',
      4: 'Emergência',
      5: 'Outro',
    } as Record<number, string>)[item.tipoOcorrenciaEnum]
      ?? 'Ocorrência ' + item.tipoOcorrenciaEnum;
  }

  private async startConnection(): Promise<void> {
    this.connectionStateSubject.next('connecting');
    const connection = new HubConnectionBuilder()
      .withUrl(environment.apiurl + '/hubs/app', {
        accessTokenFactory: () => this.auth.getAccessToken() ?? '',
      })
      .configureLogging(LogLevel.Warning)
      .withAutomaticReconnect([0, 2_000, 10_000, 30_000])
      .build();
    this.registerHandlers(connection);
    this.connection = connection;
    try {
      await connection.start();
      this.connectionStateSubject.next('connected');
    } catch (error) {
      this.connectionStateSubject.next('disconnected');
      if (this.connection === connection) this.connection = undefined;
      this.scheduleRetry();
      throw error;
    }
  }

  private registerHandlers(connection: HubConnection): void {
    POSTO_COMANDO_EVENTS.forEach((event) => {
      connection.on(event, (payload: HubPayload | HubPayload[]) =>
        this.receive(event, payload),
      );
    });
    // Compatibilidade durante a transição do evento anterior.
    connection.on('NovaOcorrencia', (payload: HubPayload | HubPayload[]) =>
      this.receive('EfetivoOcorrencia', payload),
    );
    connection.onreconnecting(() =>
      this.connectionStateSubject.next('reconnecting'),
    );
    connection.onreconnected(() =>
      this.connectionStateSubject.next('connected'),
    );
    connection.onclose(() => {
      if (this.connection === connection) {
        this.connection = undefined;
        this.connectionStateSubject.next('disconnected');
        this.scheduleRetry();
      }
    });
  }

  private receive(
    event: PostoComandoEvent,
    payload: HubPayload | HubPayload[],
  ): void {
    const values = Array.isArray(payload) ? payload : [payload];
    const current = new Map(
      this.notificationsSubject.value.map((item) => [item.key, item]),
    );
    values.map((value) => this.normalize(event, value)).forEach((item) => {
      current.set(item.key, { ...current.get(item.key), ...item });
    });
    const notifications = [...current.values()]
      .sort((a, b) =>
        new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime(),
      )
      .slice(0, 150);
    this.publish(notifications);
  }

  private normalize(
    event: PostoComandoEvent,
    payload: HubPayload,
  ): PostoComandoNotification {
    const data = this.payloadRecord(payload);
    const entityId = this.pickId(data, this.idFields(event));
    const occurredAt = this.dateValue(this.pick(data, [
      'occurredAt', 'ocorridoEm', 'dataHora', 'dataOcorrencia',
      'data', 'createdAt', 'timestamp',
    ]));
    const key = entityId != null
      ? event + ':' + entityId + ':' + occurredAt
      : event + ':' + occurredAt + ':' + ++this.sequence;
    const nomePosto = this.text(this.pick(data, ['nomePosto', 'postoNome', 'posto']));
    const nomeEfetivo = this.text(
      this.pick(data, ['nomeEfetivo', 'efetivoNome', 'nome', 'utilizador']),
    );
    const explicitDescription = this.text(
      this.pick(data, ['mensagem', 'message', 'descricao', 'description']),
    );
    const meta = EVENT_META[event];
    const type = this.number(
      this.pick(data, ['tipoOcorrenciaEnum', 'tipoOcorrencia', 'tipoEnum']),
    );
    const otherType = this.text(
      this.pick(data, ['descricaoTipoOutro', 'tipoOutro']),
    );
    const notification: PostoComandoNotification = {
      key,
      event,
      entityId,
      title: event === 'EfetivoOcorrencia'
        ? otherType || this.occurrenceType(type)
        : meta.title,
      description: explicitDescription || this.contextualDescription(
        event, meta.description, nomePosto, nomeEfetivo,
      ),
      occurredAt,
      postoId: this.number(this.pick(data, ['postoId', 'idPosto'])),
      nomePosto,
      efetivoId: this.number(this.pick(data, ['efetivoId', 'idEfetivo'])),
      nomeEfetivo,
      escalaId: this.number(this.pick(data, ['escalaId', 'idEscala'])),
      ocorrenciaId: event === 'EfetivoOcorrencia'
        ? this.number(this.pick(data, ['ocorrenciaId', 'idOcorrencia', 'id']))
        : undefined,
      criticidadeEnum: event === 'EfetivoOcorrencia'
        ? this.number(this.pick(data, [
          'criticidadeEnum', 'criticidade', 'nivelCriticidade',
        ]))
        : undefined,
      tipoOcorrenciaEnum: event === 'EfetivoOcorrencia' ? type : undefined,
      descricaoTipoOutro: event === 'EfetivoOcorrencia' ? otherType : undefined,
      lida: this.readKeys.has(key) || (
        event === 'EfetivoOcorrencia' &&
        entityId != null &&
        this.readKeys.has(String(entityId))
      ),
      payload: data,
    };
    return notification;
  }

  private contextualDescription(
    event: PostoComandoEvent,
    fallback: string,
    nomePosto?: string,
    nomeEfetivo?: string,
  ): string {
    if (event === 'EfetivoLogin' && nomeEfetivo) {
      return nomeEfetivo + ' efetuou login no sistema.';
    }
    if (nomeEfetivo && event.includes('Alimentacao')) {
      return fallback.replace(/\.$/, '') + ' por ' + nomeEfetivo + '.';
    }
    return nomePosto
      ? fallback.replace(/\.$/, '') + ' — ' + nomePosto + '.'
      : fallback;
  }

  private publish(notifications: PostoComandoNotification[]): void {
    this.notificationsSubject.next(notifications);
    this.unreadCountSubject.next(notifications.filter((item) => !item.lida).length);
    this.occurrencesSubject.next(
      notifications
        .filter((item) => item.event === 'EfetivoOcorrencia')
        .map((item) => ({
          key: item.key,
          id: item.ocorrenciaId ?? 0,
          criticidadeEnum: item.criticidadeEnum ?? 0,
          tipoOcorrenciaEnum: item.tipoOcorrenciaEnum ?? 0,
          descricaoTipoOutro: item.descricaoTipoOutro,
          dataOcorrencia: item.occurredAt,
          postoId: item.postoId ?? 0,
          nomePosto: item.nomePosto ?? '',
          nomeEfetivo: item.nomeEfetivo ?? '',
          lida: item.lida,
        })),
    );
  }

  private updateReadState(): void {
    this.publish(this.notificationsSubject.value.map((item) => ({
      ...item,
      lida: this.readKeys.has(item.key) || item.lida,
    })));
  }

  private payloadRecord(payload: HubPayload): Record<string, unknown> {
    if (payload && typeof payload === 'object') {
      const outer = payload as Record<string, unknown>;
      const nested = this.pick(outer, ['data', 'payload']);
      return nested && typeof nested === 'object' && !Array.isArray(nested)
        ? { ...outer, ...(nested as Record<string, unknown>) }
        : outer;
    }
    return payload == null ? {} : { mensagem: String(payload) };
  }

  private pick(data: Record<string, unknown>, fields: string[]): unknown {
    const entries = new Map(
      Object.entries(data).map(([key, value]) => [key.toLowerCase(), value]),
    );
    for (const field of fields) {
      const value = entries.get(field.toLowerCase());
      if (value !== undefined && value !== null && value !== '') return value;
    }
    return undefined;
  }

  private idFields(event: PostoComandoEvent): string[] {
    if (event === 'EfetivoOcorrencia') return ['ocorrenciaId', 'idOcorrencia', 'id'];
    if (event.startsWith('Escala')) return ['escalaId', 'idEscala', 'id'];
    return ['efetivoId', 'idEfetivo', 'alimentacaoId', 'idAlimentacao', 'id'];
  }

  private pickId(
    data: Record<string, unknown>,
    fields: string[],
  ): string | number | undefined {
    const value = this.pick(data, fields);
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
  }

  private number(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '') return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private text(value: unknown): string | undefined {
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
  }

  private dateValue(value: unknown): string {
    const date = value instanceof Date ? value : new Date(String(value ?? ''));
    return Number.isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString();
  }

  private occurrenceType(type?: number): string {
    return ({
      1: 'Incidente',
      2: 'Falha de equipamento',
      3: 'Intrusão',
      4: 'Emergência',
      5: 'Outra ocorrência',
    } as Record<number, string>)[type ?? -1] ?? 'Nova ocorrência';
  }

  private scheduleRetry(): void {
    if (!this.shouldBeConnected || this.retryTimer) return;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = undefined;
      if (this.shouldBeConnected) void this.connect().catch(() => undefined);
    }, 10_000);
  }

  private clearRetry(): void {
    if (!this.retryTimer) return;
    clearTimeout(this.retryTimer);
    this.retryTimer = undefined;
  }

  private restoreReadKeys(): Set<string> {
    const keys = new Set<string>();
    for (const storageKey of [this.readStorageKey, this.legacyReadStorageKey]) {
      try {
        const value = JSON.parse(localStorage.getItem(storageKey) ?? '[]');
        if (Array.isArray(value)) value.forEach((key) => keys.add(String(key)));
      } catch {
        // Armazenamento inválido não deve impedir a ligação ao Hub.
      }
    }
    return keys;
  }

  private persistReadKeys(): void {
    localStorage.setItem(
      this.readStorageKey,
      JSON.stringify([...this.readKeys].slice(-750)),
    );
  }
}
